"use client";

import type { ImgProps } from "@/components/atoms/img";

import Image from "next/image";
import { useState } from "react";
import { cn } from "@marketing-sites/utils/cn";

/**
 * This component can be accessed using `Img.Client` but should only be used if you cannot avoid using an image in a client component. Otherwise, the main `Img` component should be used.
 *
 * The main `Img` component comes with automatic LQIP generation, which is lost in this component. You can pass a low quality placeholder image manually using the `lqip` prop, or alternatively you could use a background colour or gradient as the placeholder using the className prop.
 */

export const ImgClient = ({
  src,
  fill,
  alt,
  width,
  height,
  className,
  dataTestId,
  lqip,
  priority,
  sizes,
  disableFadeIn,
  imgClassName,
  lqipClassName,
}: ImgProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const imageAttrs: Pick<ImgProps, "fill" | "width" | "height" | "sizes"> = {};

  if (!width && !height && fill) {
    imageAttrs.fill = true;
    if (sizes) {
      imageAttrs.sizes = sizes;
    }
  } else {
    imageAttrs.height = height;
    imageAttrs.width = width;
  }

  return (
    <div
      className={cn(
        "overflow-hidden bg-cover",
        fill ? "absolute inset-0 size-full" : "relative block",
        className,
      )}
      data-testid={dataTestId}
    >
      {lqip && (
        <span
          className={cn(
            "ease-out-quad absolute inset-0 -z-10 bg-cover transition-opacity duration-300",
            isLoaded ? "opacity-0" : "opacity-100",
            lqipClassName,
          )}
          style={lqip ? { backgroundImage: `url(${lqip})` } : undefined}
        ></span>
      )}
      <Image
        alt={alt}
        src={src}
        {...imageAttrs}
        priority={priority}
        loading={priority ? "eager" : "lazy"}
        onLoad={() => setIsLoaded(true)}
        className={cn(
          "ease-out-quad w-full transition-opacity duration-300",
          fill && "size-full object-cover object-center",
          isLoaded || disableFadeIn || priority ? "opacity-100" : "opacity-0",
          imgClassName,
        )}
      />
    </div>
  );
};
